import { Fragment, useEffect, useState } from "react";
import { BoxContent } from "../../../components/BoxView/BoxContent";
import { ImageContainer } from "../../../components/image/ImageContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useExternalDesignContext } from "../contexts/external-design.context";
import { ExternalMarkerWrapper } from "../wrappers/external-marker.wrapper";
import { ExternalViewSelectorWrapper } from "../wrappers/external-view-selector.wrapper";
import { Marker } from "../../design-editor2/models/marker.model";
import { DebuggablePoint } from "../../design-editor2/contexts/design.context";

const EXTERNAL_API_URL = process.env.REACT_APP_EXTERNAL_API_URL;

export function ExternalLogoPlacer() {
  const {
    selectedView,
    scaledViewInfo,
    containerRef,
    imageRef,
    onImageRefSet,
    scaledLogosOfDesign,
    placementErrorMessage,
    debug,
    lastCoverageChecked,
    debugPoints,
    config,
    apiKey,
    logosOfDesign,
    style,
    design,
  } = useExternalDesignContext();

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (data && data.type === "REQUEST_SAVE_DESIGN") {
        console.log("Received save request");
        setIsSaving(true);

        try {
          console.log(
            "Saving design with styleId:",
            style?.id,
            "design.styleId:",
            design?.styleId
          );

          const payload = {
            styleId: style?.id || design?.styleId,
            title: design.title || "",
            organizationId:
              design.customerId > 0 ? design.customerId : undefined,
            designLogos: logosOfDesign.map((l) => ({
              view: l.viewName,
              logoId: l.logoId,
              xCoordinate: l.logoX,
              yCoordinate: l.logoY,
              angleRads: l.angleRads,
            })),
          };

          const response = await fetch(
            `${EXTERNAL_API_URL}/design/create-commit`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Api-Key": apiKey,
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
            throw new Error(`Save failed: ${response.statusText}`);
          }

          const result = await response.json();

          window.parent.postMessage(
            {
              type: "DESIGN_SAVED",
              designId: result.designId,
            },
            "*"
          );
        } catch (error: any) {
          console.error("Error saving design:", error);
          window.parent.postMessage(
            {
              type: "DESIGN_SAVE_ERROR",
              error: error.message || "Unknown error",
            },
            "*"
          );
        } finally {
          setIsSaving(false);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [logosOfDesign, style, design, apiKey, placementErrorMessage]);

  const isViewSelectorTop = config?.layout?.viewSelectorPosition === "top";
  const accentColor = config?.theme?.accentColor || "#2563eb";

  return (
    <div className="flex flex-col h-full relative">
      {/* Loading Overlay */}
      {isSaving && (
        <div className="absolute inset-0 z-50 bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center">
            <svg
              className="animate-spin h-10 w-10 mb-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                style={{ color: accentColor }}
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                style={{ color: accentColor }}
              ></path>
            </svg>
            <span
              className="font-medium text-sm"
              style={{ color: accentColor }}
            >
              Saving design...
            </span>
          </div>
        </div>
      )}

      {/* View Selector (Top) */}
      {isViewSelectorTop && <ExternalViewSelectorWrapper />}

      {/* Main Design Area */}
      <div
        className={`relative w-full flex justify-center items-center overflow-hidden min-h-0 shrink-0`}
        style={{
          height: config?.layout?.heightSelectedView
            ? config.layout.heightSelectedView
            : "700px",
        }}
      >
        {placementErrorMessage && (
          <div
            className={"absolute z-30 top-0 left-0 flex space-x-1"}
            style={{ padding: config?.layout?.paddingSelectedView || "0" }}
          >
            <FontAwesomeIcon
              className={"text-[20px] text-status-pending-1 p-1.5"}
              icon={faTriangleExclamation}
            />
            <div
              className={
                "bg-[#596675] rounded text-xs text-gray-100 px-2 py-1 max-w-[180px] text-center shadow-md"
              }
            >
              {placementErrorMessage}
            </div>
          </div>
        )}

        <div
          className={
            "relative w-full h-full flex justify-center items-center overflow-hidden"
          }
          style={{
            padding: config?.layout?.paddingSelectedView || "0",
          }}
        >
          <div
            className="w-full h-full flex justify-center items-center relative"
            ref={containerRef}
          >
            {scaledViewInfo && (
              <div
                className="relative shrink-0 transition-transform duration-200"
                style={{
                  width: scaledViewInfo.width,
                  height: scaledViewInfo.height,
                }}
              >
                <ImageContainer
                  ref={imageRef}
                  onRefSet={onImageRefSet}
                  url={debug ? selectedView.maskUrl : selectedView.imageUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  className="absolute top-0 left-0 bg-contain bg-no-repeat bg-center"
                />
                <div className={"absolute top-0 left-0 h-full w-full"}>
                  {scaledLogosOfDesign
                    .filter((x) => x.viewId === selectedView.id)
                    .map((m) => (
                      <Fragment key={m.id}>
                        <ExternalMarkerWrapper key={m.id} marker={m} />
                        {debug && (
                          <MarkerDebugging
                            marker={m}
                            coverage={lastCoverageChecked}
                            points={debugPoints}
                          />
                        )}
                      </Fragment>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Selector (Bottom - Default) */}
      {!isViewSelectorTop && <ExternalViewSelectorWrapper />}
    </div>
  );
}

/**
 * Code below is used to facilitate visual debugging of the logo placer.
 */

//#region

interface MarkerDebuggingProps {
  marker: Marker;
  coverage?: Marker;
  points: DebuggablePoint[];
}
const MarkerDebugging = ({
  marker,
  coverage,
  points,
}: MarkerDebuggingProps) => (
  <>
    <DebugMarker
      key={"DebugWatermark" + marker.id}
      marker={marker}
      color="#000000"
    />
    {coverage?.id === marker.id && (
      <DebugMarker
        key={"DebugCoverage" + marker.id}
        marker={coverage}
        color="#FFFFFF"
      />
    )}
    {points.map((point, i) => (
      <DebugPoint key={i} point={point} />
    ))}
  </>
);

const DEFAULT_COLOR = "#FFFFFF";
const DEFAULT_OPACITY = 0.5;
interface DebugMarkerProps {
  marker: Marker;
  color?: string;
  opacity?: number;
}
const DebugMarker = ({ marker, color, opacity }: DebugMarkerProps) => (
  <div
    style={{
      position: "absolute",
      top: marker.y,
      left: marker.x,
      width: marker.widthPx,
      height: marker.heightPx,
      backgroundColor: color ?? DEFAULT_COLOR,
      transform: `rotate(${marker.rotationRad}rad)`,
      opacity: opacity ?? DEFAULT_OPACITY,
      borderRadius: 5,
    }}
  />
);

const DEFAULT_POINT_SIZE = 10;
interface DebugPointProps {
  point: DebuggablePoint;
}
const DebugPoint = ({ point: { x, y, color, size } }: DebugPointProps) => (
  <div
    data-x={x}
    data-y={y}
    style={{
      position: "absolute",
      zIndex: 10,
      userSelect: "none",
      top: y,
      left: x,
      width: size ?? DEFAULT_POINT_SIZE,
      height: size ?? DEFAULT_POINT_SIZE,
      backgroundColor: color ?? DEFAULT_COLOR,
      transform: `translate(-50%, -50%)`,
      borderRadius: "100%",
    }}
  />
);

//#endregion
